import * as THREE from 'three';
import { LocalCoordinates } from '@/types/course.types';
import { ShotAnimationState, PlayerInfo } from '@/types/shot.types';
import { GolfBall } from './GolfBall';
import { createParabolicCurve } from '@/three/utils/curveUtils';

/**
 * Abstract base class for shot tracers
 * Contains shared logic for animation, curve calculation, and golf ball movement
 */
export abstract class BaseShotTracer {
  protected start: LocalCoordinates;
  protected end: LocalCoordinates;
  protected apexHeight: number;
  protected color: number;
  protected curve: THREE.CatmullRomCurve3;
  protected golfBall: GolfBall;
  protected animationState: ShotAnimationState;
  protected playerInfo: PlayerInfo;
  protected startPosition: THREE.Vector3;
  protected endPosition: THREE.Vector3;
  protected currentLabelPosition: THREE.Vector3;

  constructor(
    start: LocalCoordinates,
    end: LocalCoordinates,
    apexHeight: number,
    color: number,
    playerInfo: PlayerInfo
  ) {
    this.start = start;
    this.end = end;
    this.apexHeight = apexHeight;
    this.color = color;
    this.playerInfo = playerInfo;

    // Create parabolic curve
    this.curve = createParabolicCurve(start, end, apexHeight);

    // Create golf ball
    this.golfBall = new GolfBall();

    // Initialize animation state
    this.animationState = {
      progress: 0,
      isAnimating: true,
      labelProgress: 0,
      labelAnimating: false,
    };

    // Initialize label positions
    this.startPosition = new THREE.Vector3(start.x, 0, start.z);
    this.endPosition = new THREE.Vector3(end.x, 0, end.z);
    this.currentLabelPosition = this.startPosition.clone();
  }

  /**
   * Update animation (must be called in animation loop)
   * @param deltaTime Time since last frame in seconds
   */
  public update(deltaTime: number): void {
    // Update shot animation
    if (this.animationState.isAnimating) {
      this.animationState.progress += deltaTime * 0.5; // Speed factor

      if (this.animationState.progress >= 1) {
        this.animationState.progress = 1;
        this.animationState.isAnimating = false;
        // Start label animation when shot completes
        this.animationState.labelAnimating = true;
      }

      // Update path drawing (implemented by subclasses)
      this.updatePath();

      // Update golf ball position and rotation
      this.updateGolfBall();
    }

    // Update label animation
    if (this.animationState.labelAnimating) {
      const labelAnimationDuration = 1.5; // seconds
      this.animationState.labelProgress += deltaTime / labelAnimationDuration;

      if (this.animationState.labelProgress >= 1) {
        this.animationState.labelProgress = 1;
        this.animationState.labelAnimating = false;
      }

      // Smooth interpolation with easing
      const easedProgress = this.easeOutCubic(this.animationState.labelProgress);
      this.currentLabelPosition.lerpVectors(
        this.startPosition,
        this.endPosition,
        easedProgress
      );
    }
  }

  /**
   * Update golf ball position and rotation along the curve
   */
  protected updateGolfBall(): void {
    const position = this.curve.getPointAt(this.animationState.progress);
    this.golfBall.setPosition(position);

    // Rotate golf ball to simulate spin
    if (this.animationState.progress < 0.99) {
      const tangent = this.curve.getTangentAt(this.animationState.progress);
      this.golfBall.setOrientation(tangent);
    }
  }

  /**
   * Ease out cubic function for smooth label animation
   */
  protected easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Get current label position for screen projection
   */
  public getLabelPosition(): THREE.Vector3 {
    return this.currentLabelPosition;
  }

  /**
   * Get player information
   */
  public getPlayerInfo(): PlayerInfo {
    return this.playerInfo;
  }

  /**
   * Get team color
   */
  public getColor(): number {
    return this.color;
  }

  /**
   * Get golf ball group for adding to scene
   */
  public getGolfBallGroup(): THREE.Group {
    return this.golfBall.getGroup();
  }

  /**
   * Check if animations are complete
   */
  public isComplete(): boolean {
    return (
      !this.animationState.isAnimating && !this.animationState.labelAnimating
    );
  }

  /**
   * Abstract method to update path drawing
   * Must be implemented by subclasses
   */
  protected abstract updatePath(): void;

  /**
   * Abstract method to get mesh/line for adding to scene
   * Must be implemented by subclasses
   */
  public abstract getPathObject(): THREE.Object3D;

  /**
   * Abstract method for cleanup
   * Must be implemented by subclasses
   */
  public abstract dispose(): void;
}
